/**
 * Modelo que representa un mensaje en el chat
 */
class Message {
  /**
   * Crea una instancia de Message
   * @param {Object} config - Configuración del mensaje
   * @param {string} config.id - Identificador único del mensaje
   * @param {string} config.text - Contenido del mensaje
   * @param {string} config.sender - Remitente del mensaje ('user' o 'assistant')
   * @param {Date} config.timestamp - Fecha y hora del mensaje
   * @param {Object} [config.metadata] - Metadatos adicionales del mensaje
   */
  constructor({ id, text, sender, timestamp, metadata = {} }) {
    this.id = id || Date.now().toString();
    this.text = text;
    this.sender = sender; // 'user' o 'assistant'
    this.timestamp = timestamp || new Date();
    this.metadata = metadata;
  }

  /**
   * Crea una instancia de Message desde un objeto plano
   * @param {Object} data - Datos del mensaje
   * @returns {Message} Instancia de Message
   */
  static fromObject(data) {
    return new Message({
      id: data.id,
      text: data.text,
      sender: data.sender,
      timestamp: data.timestamp instanceof Date ? data.timestamp : new Date(data.timestamp),
      metadata: data.metadata || {},
    });
  }

  /**
   * Convierte la instancia a un objeto plano
   * @returns {Object} Objeto plano con las propiedades del mensaje
   */
  toObject() {
    return {
      id: this.id,
      text: this.text,
      sender: this.sender,
      timestamp: this.timestamp,
      metadata: this.metadata,
    };
  }

  /**
   * Crea un mensaje de usuario
   * @param {string} text - Contenido del mensaje
   * @param {Object} [metadata] - Metadatos adicionales
   * @returns {Message} Nueva instancia de Message
   */
  static createUserMessage(text, metadata = {}) {
    return new Message({
      text,
      sender: 'user',
      metadata,
    });
  }

  /**
   * Crea un mensaje del asistente
   * @param {string} text - Contenido del mensaje
   * @param {Object} [metadata] - Metadatos adicionales
   * @returns {Message} Nueva instancia de Message
   */
  static createAssistantMessage(text, metadata = {}) {
    return new Message({
      text,
      sender: 'assistant',
      metadata,
    });
  }
}

export default Message;
